/* eslint-disable @typescript-eslint/no-use-before-define */
import {
    Action,
    ActionReducerMap,
    createFeatureSelector,
    createSelector,
    StoreModule,
} from '@ngrx/store';
import {
    PremiumPlace,
    Sponsor,
} from '@polaris-customerweb/sharedcomponents.map';
import { Club } from './club';
import {
    AddSponsor,
    ClubAction,
    RemoveSponsor,
    SetActiveSponsor,
    SetAllPremiumPlaces,
    SetEditableSponsor,
    SetOwnClubs,
    SetOwnSponsors,
    UpdateSponsor,
} from './club.actions';

export interface ClubState {
    activeSponsor: Sponsor;
    editableSponsor: Sponsor | undefined;
    ownClubs: Club[];
    ownSponsors: Sponsor[];
    premiumPlaces: PremiumPlace[];
}

export const reducerMap: ActionReducerMap<ClubState> = {
    activeSponsor: activeSponsorReducer,
    editableSponsor: editableSponsorReducer,
    ownClubs: ownClubsReducer,
    ownSponsors: ownSponsorsReducer,
    premiumPlaces: premiumPlacesReducer,
};

export const selectClubState = createFeatureSelector<ClubState>('club');
export const selectActiveSponsor = createSelector(
    selectClubState,
    (state) => state.activeSponsor
);
export const selectAllPremiumPlaces = createSelector(
    selectClubState,
    (state) => state.premiumPlaces
);
export const selectEditableSponsor = createSelector(
    selectClubState,
    (state) => state.editableSponsor
);
export const selectHasActiveClubs = createSelector(selectClubState, (state) => {
    return (
        state.ownClubs.filter((club) => club.status === 'Approved').length > 0
    );
});
export const selectOwnClubs = createSelector(
    selectClubState,
    (state) => state.ownClubs
);
export const selectOwnSponsors = createSelector(
    selectClubState,
    (state) => state.ownSponsors
);
export const selectOwnPrimaryClub = createSelector(selectClubState, (state) => {
    return state.ownClubs.filter((club) => club.status === 'Approved')[0];
});

export const clubStoreModule = StoreModule.forFeature('club', reducerMap);

export function activeSponsorReducer(state: Sponsor, action: Action): Sponsor {
    switch (action.type) {
        case ClubAction.setActiveSponsor:
            return (action as SetActiveSponsor).sponsor?.clone();
        default:
            return state;
    }
}

export function editableSponsorReducer(
    state: Sponsor | undefined,
    action: Action
): Sponsor | undefined {
    switch (action.type) {
        case ClubAction.setEditableSponsor:
            return (action as SetEditableSponsor).sponsor?.clone();
        default:
            return state;
    }
}

export function ownClubsReducer(state: Club[] = [], action: Action): Club[] {
    switch (action.type) {
        case ClubAction.setOwnClubs:
            return [...(action as SetOwnClubs).clubs];
        default:
            return state;
    }
}

export function ownSponsorsReducer(
    state: Sponsor[] = [],
    action: Action
): Sponsor[] {
    switch (action.type) {
        case ClubAction.setOwnSponsors:
            return [...(action as SetOwnSponsors).sponsors];
        case ClubAction.addSponsor:
            return [...state, (action as AddSponsor).sponsor];
        case ClubAction.updateSponsor:
            return [
                ...state.filter(
                    (sponsor) =>
                        sponsor.id !==
                        (action as UpdateSponsor).sponsor.clone().id
                ),
                (action as UpdateSponsor).sponsor.clone(),
            ];
        case ClubAction.removeSponsor:
            return state.filter(
                (sponsor) => sponsor.id !== (action as RemoveSponsor).sponsorId
            );
        default:
            return state;
    }
}

export function premiumPlacesReducer(
    state: PremiumPlace[] = [],
    action: Action
): PremiumPlace[] {
    let updatedPremiumPlace;

    switch (action.type) {
        case ClubAction.setAllPremiumPlaces:
            return [...(action as SetAllPremiumPlaces).premiumPlaces];
        case ClubAction.addSponsor:
            return [...state, (action as AddSponsor).sponsor.toPremiumPlace()];
        case ClubAction.updateSponsor:
            updatedPremiumPlace = (
                action as UpdateSponsor
            ).sponsor.toPremiumPlace();
            return [
                ...state.filter(
                    (premiumPlace) => premiumPlace.id !== updatedPremiumPlace.id
                ),
                updatedPremiumPlace,
            ];
        case ClubAction.removeSponsor:
            return state.filter(
                (premiumPlace) =>
                    premiumPlace.id !== (action as RemoveSponsor).sponsorId
            );
        default:
            return state;
    }
}
